import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-account-deletion',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzAlertModule,
    NzSelectModule,
    TranslateModule
  ],
  templateUrl: './account-deletion.component.html',
  styleUrl: './account-deletion.component.less'
})
export class AccountDeletionComponent implements OnDestroy {
  form: FormGroup;
  isDeleting = false;
  isDeactivating = false;

  confirmationPhrase = 'DELETE MY ACCOUNT';
  deletionCooldownDays = 30;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      reason: ['', [Validators.required]],
      feedback: [''],
      confirmationText: ['', [Validators.required, this.confirmationValidator.bind(this)]]
    });

    this.translate
      .get('ACCOUNT_DELETION.CONFIRMATION_PHRASE')
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.confirmationPhrase = value;
        this.form.get('confirmationText')?.updateValueAndValidity({ emitEvent: false });
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const value = this.translate.instant('ACCOUNT_DELETION.CONFIRMATION_PHRASE');
        this.confirmationPhrase = value;
        this.form.get('confirmationText')?.updateValueAndValidity({ emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirmationValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value || '').toString().trim().toLowerCase();
    return value === this.confirmationPhrase.toLowerCase() ? null : { confirmationMismatch: true };
  }

  deactivate(): void {
    this.isDeactivating = true;
    this.authService
      .deactivateAccount()
      .pipe(finalize(() => (this.isDeactivating = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('ACCOUNT_DELETION.DEACTIVATED_SUCCESS'));
          this.authService.logout();
        },
        error: (error) =>
          this.message.error(error?.message || this.translate.instant('ACCOUNT_DELETION.ERROR_GENERIC'))
      });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isDeleting = true;

    const payload = {
      reason: this.form.value.reason,
      feedback: this.form.value.feedback,
      confirmationText: this.form.value.confirmationText
    };

    this.authService
      .requestAccountDeletion(payload)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('ACCOUNT_DELETION.SCHEDULED_SUCCESS'));
          this.authService.logout();
        },
        error: (error) =>
          this.message.error(error?.message || this.translate.instant('ACCOUNT_DELETION.ERROR_GENERIC'))
      });
  }
}
